import React, { useCallback, useState, useEffect } from 'react';
import { gql } from 'apollo-boost';
import { useClient } from 'shared/apollo';
import LinearProgress from '@mui/material/LinearProgress';
import { useHistory } from 'react-router-dom';

import { env } from 'shared/apollo/client';
import { useUpdateUser } from 'shared/apollo';
import { useUser } from 'shared/auth';
import AccountEditor from 'shared/no-auth/components/AccountEditor/AccountEditor';
import { useLambda, invokeLambda } from 'shared/services/lambda';

export default function EditUser({ match: { params: { username } } }) {
  return <EditUserInner
    {...{ username }}
    title="Edit User"
    canChangePassword
  />;
}

export const EditUserInner = ({
  onDirty,
  username,
  title,
  canChangePassword,
  extraButtons,
  selectedUser = {},
  setSelectedUser = () => { /* noop */ },
  isDrawer = false,
  refreshUserList = () => { /* noop */ },
  width,
  accountId,
}) => {
  const client = useClient();
  //  const { userAttributes, handleSubmit, handleEmailVerified } = useUserAttributes()
  const { push } = useHistory();
  const { signInAs } = useUser() || {};

  const [errorSubmit, setErrorSubmit] = useState('');
  const [updateCompleted, setUpdateCompleted] = useState(false);

  useEffect(() => {
    if (isDrawer) document.body.style.overflow = 'hidden';
    return () => {
      if (isDrawer) document.body.style.overflow = 'auto';
    };
  }, []);

  const { run: runUpdate, isLoading: loadingUpdate } = useUpdateUser();
  const handleEmailVerified = useCallback(() => { /* noop */ }, []);
  const { result: user, error } = useLambda('users/getUser', {
    env,
    username
  });
  const notOnCognito = !!error;
  const { userAttributes } = user || {};

  const handleSubmit = useCallback(async ({ sub, ...values }) => {
    setErrorSubmit();
    const shouldUpdateCognito = !notOnCognito;

    if (!values.phone_number) {
      delete values.phone_number_verified;
    }

    const shouldCheckEmail = (shouldUpdateCognito && values?.email !== userAttributes.email);
    if (shouldCheckEmail) {
      push(`/account/edit-user/${values.email}`);
      signInAs(values.email);
    }


    // keep pass values
    const password = values['new_password'];
    const oldPpassword = values['old_password'];
    delete values['new_password'];
    delete values['old_password'];

    if (shouldUpdateCognito && password && oldPpassword) {
      handleChangePassword(oldPpassword, password);
    }

    // Only update cognito if user has sign in already, otherwise the user does not exist yet on cognito
    if (shouldUpdateCognito) {
      const cognitoUpdateRequest = await invokeLambda('users/updateUserAttributes', {
        env,
        username: user.username,
        userAttributes: values,
      });
      if (cognitoUpdateRequest?.error) {
        let errorMsg = cognitoUpdateRequest?.error;
        if (errorMsg === 'Invalid phone number format.') {
          errorMsg += ' Must include country code (+1XXXXXXXXXX).';
        }
        setErrorSubmit(errorMsg);
        return;
      }
    }

    await runUpdate({
      id: parseInt(values['custom:user_id']),
      firstName: values.given_name,
      lastName: values.family_name,
      title: values['custom:title'],
      pronoun: values['custom:pronoun'],
      phone: values.phone_number,
      userType: values['custom:type'],
    });

    // should refresh user list
    setUpdateCompleted('User successfully updated');
    refreshUserList();

    if (notOnCognito) {
      setSelectedUser({
        ...selectedUser,
        firstName: values.given_name,
        lastName: values.family_name,
        phone: values.phone_number,
        pronoun: values['custom:pronoun'],
        title: values['custom:title'],
        userType: values['custom:type'],
      });
    }

    if (shouldCheckEmail) {
      alert(`Only the user can login and use verification code at ${values.email}!`);
    }
  }, [user, push, signInAs, notOnCognito]);
  const handleChangePassword = useCallback(async (oldPassword, password) =>
    invokeLambda('users/setUserPassword', {
      env,
      username: user.username,
      password,
      permanent: true,
    })
    , [user]);

  if (!user && error === undefined) {
    return <LinearProgress variant="query" />;
  }


  const userWithDefaultAttributes = (userAttributes) ?
    {
      ...userAttributes,
      'custom:pronoun': userAttributes?.['custom:pronoun'] || selectedUser?.pronoun || '',
      'phone_number': userAttributes?.['phone_number'] || selectedUser?.phone || '',
    }
    :
    {
      'custom:pronoun': selectedUser?.pronoun ?? '',
      'custom:title': selectedUser?.title ?? '',
      'custom:type': selectedUser?.userType ?? '',
      'custom:user_id': selectedUser?.userId,
      'email': selectedUser?.email,
      'family_name': selectedUser?.lastName ?? '',
      'given_name': selectedUser?.firstName ?? '',
      'phone_number': selectedUser?.phone ?? '',
    };

  const handleResendInvitation = async (variables) => {
    try {
      await client.mutate({
        mutation: resendUserInvitationMutation,
        variables: {
          ...variables,
          accountId
        },
      });
      setUpdateCompleted(`Invtitation successfully resent to ${variables.email}!`);
    } catch (err) {
      alert(err.message || err);
      return false;
    }
  };

  return (
    <AccountEditor
      onDirty={onDirty}
      submitButtonTitle="Save"
      onChangePassword={canChangePassword ? handleChangePassword : undefined}
      mode="edit"
      isDrawer={isDrawer}
      errorText={errorSubmit}
      notOnCognito={notOnCognito}
      {...{ userAttributes: userWithDefaultAttributes, handleSubmit, handleEmailVerified, title, extraButtons, width }}
      updateCompleted={updateCompleted}
      handleResendInvitation={handleResendInvitation}
    />
  );
};


const resendUserInvitationMutation = gql`mutation resendUserInvitation(
  $email: String!,
  $firstName: String!,
  $lastName: String!,
  $accountId: Int,
) {
  resendUserInvitation(
    email: $email,
    firstName: $firstName,
    lastName: $lastName,
    accountId: $accountId
  ) {
    success
  }
}`;